import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCMS } from '../context/CMSContext';
import {
  Package, Users, Settings, Pencil, Loader2, LogOut, ArrowLeft, FileText, Receipt,
} from 'lucide-react';
import ContentEditor from './admin/ContentEditor';
import OrdersTab from './admin/OrdersTab';
import CustomersTab from './admin/CustomersTab';
import ProductsTab from './admin/ProductsTab';
import StaffTab from './admin/StaffTab';
import SettingsTab from './admin/SettingsTab';

const TABS = [
  { value: 'orders',    label: 'Orders',    icon: Receipt },
  { value: 'customers', label: 'Customers', icon: Users },
  { value: 'products',  label: 'Products',  icon: Package },
  { value: 'content',   label: 'Content',   icon: FileText },
  { value: 'staff',     label: 'Staff',     icon: Users },
  { value: 'settings',  label: 'Settings',  icon: Settings },
];

const TAB_COMPONENT = {
  orders:    OrdersTab,
  customers: CustomersTab,
  products:  ProductsTab,
  content:   ContentEditor,
  staff:     StaffTab,
  settings:  SettingsTab,
};

const TabsBar = ({ tabs, current, onChange }) => (
  <div className="flex items-center gap-1 sm:gap-2 border-b border-slate-200 overflow-x-auto">
    {tabs.map(({ value, label, icon: Icon }) => (
      <button
        key={value}
        onClick={() => onChange(value)}
        className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-3 text-sm font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap ${
          current === value
            ? 'text-red-700 border-red-600'
            : 'text-slate-500 hover:text-slate-700 border-transparent'
        }`}
      >
        <Icon size={15} /> {label}
      </button>
    ))}
  </div>
);

const AdminHeader = ({ user, onEditLive, onLogout }) => (
  <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-red-700">
        <ArrowLeft size={15} /> Back to site
      </Link>
      <div className="flex items-center gap-2">
        <button onClick={onEditLive} className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-semibold rounded-lg">
          <Pencil size={13} /> Edit live page
        </button>
        <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-slate-100">
          <div className="w-7 h-7 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center uppercase">{(user?.name || user?.email || '?').charAt(0)}</div>
          <span className="text-xs text-slate-700 font-medium hidden sm:block">{user?.name || user?.email}</span>
        </div>
        <button onClick={onLogout} className="p-2 text-slate-500 hover:text-red-600 rounded-lg" title="Sign out"><LogOut size={16} /></button>
      </div>
    </div>
  </header>
);

const AdminPage = () => {
  const { isAuthed, loading, logout, user } = useAuth();
  const { setEditMode } = useCMS();
  const [tab, setTab] = useState('orders');

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-red-600" size={28} /></div>;
  }
  if (!isAuthed) return <Navigate to="/login" replace />;

  const handleEditLive = () => { setEditMode(true); window.location.href = '/'; };
  const TabBody = TAB_COMPONENT[tab];

  return (
    <main className="min-h-screen bg-slate-50">
      <AdminHeader user={user} onEditLive={handleEditLive} onLogout={logout} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0a0a0a]">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Manage products, staff, and your account.</p>
        <div className="mt-6">
          <TabsBar tabs={TABS} current={tab} onChange={setTab} />
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {TabBody && <TabBody />}
      </div>
    </main>
  );
};

export default AdminPage;
